import React, { useState } from "react";
import { Backdrop, Divider, Fade, Grid, Modal, Typography } from "@material-ui/core";
import BasicSelect from "../../custom/BasicSelect";
import { moveDealToPipelineStyle } from "./moveDealToPipelineStyle";
import useGetPipelineAndStage from "./hook/useGetPipelineAndStage";
import { Skeleton } from "@material-ui/lab";
import BasicSelectWithChip from "./BasicSelectWithChip";
import { assignBulkStage, contactMoveStage, dealMoveToStageApi } from "../../addContact/contactApi";

const ACTION_TYPES = {
  CONTACT_MOVE_STAGE: "CONTACT_MOVE_STAGE",
  DEAL_MOVE_STAGE: "DEAL_MOVE_STAGE"
}

const MoveDealToPipeline = ({ open, data = {}, onClose }) => {
  const [selectedPipelineId, setSelectedPipelineId] = useState(data?.selectedPipelineId || null);
  const [selectedStageId, setSelectedStageId] = useState(data?.selectedStageId ||null);
  const [error, setError] = useState({ pipeline: false, stage: false });

  const [isLoading, setIsLoading] = useState(false);

  const { pipelineList, stages, isLoadingPipelines, isLoadingStages } =
    useGetPipelineAndStage(selectedPipelineId);

  const classes = moveDealToPipelineStyle();

  const handleClose = () => {
    onClose();
    if(data?.onClose){
      data.onClose();
    }
  };

  const isValidForm = () => {
    const newError = {
      pipeline: !selectedPipelineId,
      stage: !selectedStageId,
    };

    setError(newError);

    return !(newError.pipeline || newError.stage);
  };

  const handleSubmit = async () => {
    if (!isValidForm()) return;

    try {
      setIsLoading(true);

      if (data?.type === ACTION_TYPES.CONTACT_MOVE_STAGE) {
        if (data?.contactIds[0] && data?.source_stage === undefined) {
          const response = await assignBulkStage({
            stageId: selectedStageId,
            contactIds: data?.contactIds,
          });

          if (response.data.success) {
            window.showNotification("SUCCESS", response.data.html);
            handleClose();
          } else {
            window.showNotification("ERROR", response.data.html);
          }
        } 
        else if(data?.source_stage !== undefined) {
          const response = await contactMoveStage(
            {
              destination_stage: `stage-${selectedStageId}`,
              source_stage: `stage-${data?.source_stage}`,
              order: 0,
              stage_id: data?.stage_id,
            }
          );

          data.callback(response.data);
          handleClose();
        }
      } else if(data?.type === ACTION_TYPES.DEAL_MOVE_STAGE) {
        const response = await dealMoveToStageApi({
          contact_stage_id: data?.dealToMove,
          selectedPipeline: selectedPipelineId,
          selectedStage: selectedStageId,
        });

        if (response) {
          data.callback({
            type: "SUCCESS",
          });
          handleClose();
        } else {
          data.callback({
            type: "ERROR",
          });
        }
      }
    } catch (error) {
      console.error("Error submitting stage:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.modalHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img
                width={50}
                src={`${process.env.REACT_APP_CDN_LINK}assets/images/releases/1.0.0/move_pipeline_87989.png`}
                alt=""
              />

              <Typography style={{ height: 'unset !important', color: 'black', fontWeight: 600,}} variant="h5">Move Deal To Another Pipeline</Typography>
            </div>
          </div>
          <Divider />

          <div className={classes.modalBody} style={{ minHeight: 200 }}>
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              Select the pipeline and stage you'd like to move the deal to:
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {isLoadingPipelines ? (
                  <Skeleton
                    variant="rect"
                    width="100%"
                    height={38}
                    style={{ borderRadius: "8px" }}
                  />
                ) : (
                  <div>
                    <BasicSelectWithChip
                      fullWidth
                      name="pipeline"
                      defaultText="Select a pipeline"
                      value={selectedPipelineId || ""}
                      options={pipelineList}
                      onChange={(event) => {
                        setSelectedPipelineId(event.target.value);
                        setSelectedStageId(null);
                        setError((prev) => ({ ...prev, pipeline: false }));
                      }}
                      disabled={isLoading}
                      mapping={{
                        label: "title",
                        value: "id",
                      }}
                    />

                    {error.pipeline && (
                      <div className={classes.errorText}>
                        Please select a pipeline first
                      </div>
                    )}
                  </div>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {isLoadingStages ? (
                  <Skeleton
                    variant="rect"
                    width="100%"
                    height={38}
                    style={{ borderRadius: "4px" }}
                  />
                ) : (
                  <div>
                    <BasicSelect
                      fullWidth
                      name="stage"
                      defaultText="Select a stage"
                      value={selectedStageId || ""}
                      options={stages}
                      onChange={(event) => {
                        setSelectedStageId(event.target.value);
                        setError((prev) => ({ ...prev, stage: false }));
                      }}
                      disabled={isLoading}
                      mapping={{
                        label: "label",
                        value: "value",
                      }}
                    />

                    {!error.pipeline && error.stage && (
                      <div className={classes.errorText}>
                        Please choose a stage
                      </div>
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </div>

          <div className={classes.modalFooter}>
            <div
              className={classes.actionContainer}
              style={{ display: "flex", alignItems: "center", gap: 16 }}
            >
              <button
                onClick={handleClose}
                className={`${classes.button} ${classes.closeButton}`}
                style={{ padding: "8px 24px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={`${classes.button} ${classes.confirmButton} ${
                  isLoading ? classes.disabled : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Saving" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default MoveDealToPipeline;
